<template>
    <div class="legal-page">
        <div class="legal-container">
            <header class="legal-header">
                <router-link to="/login" class="back-link">
                    <i class="bi bi-arrow-left"></i> Back
                </router-link>
                <TenantLogo id="legal-logo" logo-class="legal-logo" />
                <h1>Privacy Policy</h1>
                <p class="last-updated">Last Updated: {{ lastUpdated }}</p>
            </header>

            <main class="legal-content">
                <section>
                    <h2>1. Introduction</h2>
                    <p>
                        Welcome to {{ clubName }} ("we," "our," or "us"). We are committed to protecting your personal
                        information and your right to privacy. This Privacy Policy explains how we collect, use,
                        disclose,
                        and safeguard your information when you use our {{ appName }} application and services.
                    </p>
                    <p>
                        Please read this privacy policy carefully. If you do not agree with the terms of this privacy
                        policy,
                        please do not access the application.
                    </p>
                </section>

                <section>
                    <h2>2. Information We Collect</h2>
                    <h3>2.1 Personal Information</h3>
                    <p>We may collect personal information that you voluntarily provide to us when you register with {{
                        clubName }}, including:</p>
                    <ul>
                        <li>Name and contact information (email address, phone number)</li>
                        <li>Date of birth</li>
                        <li>Address and postcode</li>
                        <li>Emergency contact details</li>
                        <li>Medical information and health conditions (where relevant to your participation)</li>
                        <li>Playing position and football experience</li>
                        <li>Photographs and video footage</li>
                        <li>Performance and training data</li>
                    </ul>

                    <h3>2.2 Automatically Collected Information</h3>
                    <p>When you access {{ appName }}, we may automatically collect certain information, including:</p>
                    <ul>
                        <li>Device information (browser type, operating system)</li>
                        <li>IP address and location data</li>
                        <li>Usage data and analytics</li>
                        <li>Cookies and similar tracking technologies</li>
                    </ul>
                </section>

                <section>
                    <h2>3. How We Use Your Information</h2>
                    <p>{{ clubName }} uses the information we collect for the following purposes:</p>
                    <ul>
                        <li>To create and manage your account</li>
                        <li>To facilitate training sessions and match participation</li>
                        <li>To communicate with you about schedules, events, and club activities</li>
                        <li>To process payments and manage subscriptions</li>
                        <li>To track player development and performance</li>
                        <li>To ensure the safety and wellbeing of all participants</li>
                        <li>To comply with legal obligations</li>
                        <li>To improve our services and user experience</li>
                        <li>To understand app engagement — for example when the app is
                            opened — so coaches and administrators can support members
                            who may need help getting started</li>
                    </ul>
                </section>

                <section>
                    <h2>4. Data Sharing and Disclosure</h2>
                    <p>We may share your information in the following circumstances:</p>
                    <ul>
                        <li><strong>With coaches and staff:</strong> To facilitate training and player development</li>
                        <li><strong>With governing bodies:</strong> Where required for registration with football
                            associations (e.g., SFA, FIFA)</li>
                        <li><strong>With medical professionals:</strong> In case of injury or medical emergency</li>
                        <li><strong>With parents/guardians:</strong> For players under 18 years of age</li>
                        <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
                    </ul>
                    <p>We do not sell your personal information to third parties.</p>
                </section>

                <section>
                    <h2>5. Data Security</h2>
                    <p>
                        We implement appropriate technical and organisational security measures to protect your personal
                        information against unauthorised access, alteration, disclosure, or destruction. However, no
                        method
                        of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                    </p>
                </section>

                <section>
                    <h2>6. Data Retention</h2>
                    <p>
                        We retain your personal information for as long as necessary to fulfill the purposes outlined in
                        this
                        Privacy Policy, unless a longer retention period is required by law. When you leave {{ clubName
                        }},
                        we will retain your data for a reasonable period to comply with legal obligations and resolve
                        disputes.
                    </p>
                </section>

                <section>
                    <h2>7. Your Rights</h2>
                    <p>Under {{ legal.privacyLaw }}, you have the following rights:</p>
                    <ul>
                        <li><strong>Right of access:</strong> Request a copy of your personal data</li>
                        <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
                        <li><strong>Right to erasure:</strong> Request deletion of your data ("right to be forgotten")
                        </li>
                        <li><strong>Right to restrict processing:</strong> Request limitation of data processing</li>
                        <li><strong>Right to data portability:</strong> Request transfer of your data</li>
                        <li><strong>Right to object:</strong> Object to processing of your data</li>
                        <li><strong>Right to withdraw consent:</strong> Withdraw consent at any time</li>
                    </ul>
                    <p>
                        To exercise any of these rights, please contact us using the details provided below. If you
                        are not satisfied with our response, you may complain to {{ legal.regulator }}.
                    </p>
                </section>

                <section>
                    <h2>8. Children's Privacy</h2>
                    <p>
                        {{ clubName }} may collect information from children under 18 with parental or guardian consent.
                        Parents and guardians have the right to review, delete, or refuse further collection of their
                        child's information. We take additional precautions to protect the privacy of young players.
                    </p>
                </section>

                <section>
                    <h2>9. Cookies</h2>
                    <p>
                        We use cookies and similar tracking technologies to track activity on our application and store
                        certain information. You can instruct your browser to refuse all cookies or indicate when a
                        cookie
                        is being sent. However, some features of the application may not function properly without
                        cookies.
                    </p>
                </section>

                <section>
                    <h2>10. Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of any changes by
                        posting
                        the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to
                        review
                        this Privacy Policy periodically for any changes.
                    </p>
                </section>

                <section>
                    <h2>11. Contact Us</h2>
                    <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
                    <div class="contact-info">
                        <p><strong>{{ clubName }}</strong></p>
                        <p v-if="legal.contactUrl">
                            Web enquiry: <a :href="legal.contactUrl" target="_blank" rel="noopener">{{ legal.contactUrl }}</a>
                        </p>
                        <p v-else-if="legal.region === 'AU'">Please use the enquiry form on the club website.</p>
                        <p v-else>Email: <a :href="contactMailto">{{ contactEmail }}</a></p>
                    </div>
                </section>
            </main>

            <footer class="legal-footer">
                <p>&copy; {{ currentYear }} {{ clubName }}. All rights reserved.</p>
            </footer>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import TenantLogo from '@/components/TenantLogo/index.vue'
import { getLegalConfig } from '@/config/tenant'

export default {
    name: 'PrivacyPolicy',
    components: {
        TenantLogo
    },
    setup() {
        const store = useStore()

        const clubName = computed(() => store.getters.clubName || 'The Club')
        const appName = computed(() => store.getters.appName || 'Player App')
        const clubNameShort = computed(() => store.getters.clubNameShort || 'Club')

        const currentYear = new Date().getFullYear()
        const lastUpdated = 'October 2026'
        const legal = getLegalConfig()

        // Central data controller contact for privacy / data requests (all tenants).
        const contactEmail = 'bigs@stewartpaxton.co.uk'
        const contactMailto = computed(() =>
            `mailto:${contactEmail}?subject=${encodeURIComponent('Privacy enquiry - ' + clubName.value)}`
        )

        return {
            clubName,
            appName,
            clubNameShort,
            currentYear,
            lastUpdated,
            legal,
            contactEmail,
            contactMailto
        }
    }
}
</script>

<style lang="scss" scoped>
.legal-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    padding: 2rem;
}

.legal-container {
    max-width: 900px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.legal-header {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
    color: white;
    padding: 2rem;
    text-align: center;
    position: relative;

    .back-link {
        position: absolute;
        left: 1.5rem;
        top: 1.5rem;
        color: white;
        text-decoration: none;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.9rem;
        opacity: 0.8;
        transition: opacity 0.2s;

        &:hover {
            opacity: 1;
        }
    }

    h1 {
        margin: 1rem 0 0.5rem;
        font-size: 2rem;
    }

    .last-updated {
        opacity: 0.7;
        font-size: 0.9rem;
    }
}

.legal-content {
    padding: 2rem 3rem;

    section {
        margin-bottom: 2rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    h2 {
        color: #1a1a2e;
        font-size: 1.4rem;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 2px solid #e0e0e0;
    }

    h3 {
        color: #333;
        font-size: 1.1rem;
        margin: 1.5rem 0 0.75rem;
    }

    p {
        color: #555;
        line-height: 1.7;
        margin-bottom: 1rem;
    }

    ul {
        margin: 1rem 0;
        padding-left: 1.5rem;

        li {
            color: #555;
            line-height: 1.7;
            margin-bottom: 0.5rem;
        }
    }

    .contact-info {
        background: #f8f9fa;
        padding: 1.5rem;
        border-radius: 8px;
        margin-top: 1rem;

        p {
            margin-bottom: 0.5rem;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

.legal-footer {
    background: #f8f9fa;
    padding: 1.5rem;
    text-align: center;
    border-top: 1px solid #e0e0e0;

    p {
        color: #666;
        font-size: 0.9rem;
        margin: 0;
    }
}

#legal-logo {
    max-width: 120px;
    margin: 0 auto;
}

@media (max-width: 768px) {
    .legal-page {
        padding: 1rem;
    }

    .legal-content {
        padding: 1.5rem;
    }

    .legal-header {
        .back-link {
            position: static;
            margin-bottom: 1rem;
            justify-content: center;
        }
    }
}
</style>
